<template>
  <div class="px-6 py-4" style="max-height: 80vh; overflow-y: auto;">
    <!-- Filters Section -->
    <div class="mb-4 bg-gray-50 dark:bg-dark-700 p-3 rounded-lg border border-gray-200 dark:border-dark-600">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Search -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Search</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search by client or order #"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @input="debouncedFetch"
          />
        </div>
        
        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Sort By</label>
          <select
            v-model="filters.sort"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchData"
          >
            <option value="oldest">Oldest First</option>
            <option value="latest">Latest First</option>
            <option value="amount_high">Amount (High to Low)</option>
            <option value="amount_low">Amount (Low to High)</option>
          </select>
        </div>
        
        <!-- Urgency -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Urgency</label>
          <select
            v-model="filters.urgency"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchData"
          >
            <option value="all">All</option>
            <option value="critical">Critical (90+ days)</option>
            <option value="urgent">Urgent (60-90 days)</option>
            <option value="attention">Needs Attention (30-60 days)</option>
            <option value="normal">Normal (0-30 days)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="overflow-x-auto border border-gray-200 dark:border-dark-600 rounded-lg shadow-sm mb-4">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
        <thead class="bg-gray-50 dark:bg-dark-700">
          <tr>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Client</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Order #</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Total</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Paid</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Due</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Age</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
          <template v-if="loading">
            <tr>
              <td colspan="7" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">Loading follow-ups...</td>
            </tr>
          </template>
          <template v-else-if="followUps.length === 0">
            <tr>
              <td colspan="7" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">No payment follow-ups found</td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="followup in followUps" :key="followup.id" class="hover:bg-gray-50 dark:hover:bg-dark-700">
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900 dark:text-dark-100">
                  {{ followup.clientName }}
                </div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm text-gray-500 dark:text-dark-400">{{ followup.orderNumber }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-dark-100">₱{{ formatPrice(followup.totalAmount) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-dark-100">₱{{ formatPrice(followup.amountPaid) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900 dark:text-dark-100">₱{{ formatPrice(followup.amountDue) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                      :class="getUrgencyClass(followup.days_old)">
                  {{ followup.days_old }} days
                </span>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-dark-400">
                <button @click="viewOrder(followup.id)" class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3">
                  <i class="fas fa-eye"></i>
                </button>
                <button @click="recordPayment(followup.id)" class="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300">
                  <i class="fas fa-money-bill-wave"></i>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between border-t border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-4 py-3 sm:px-6">
      <div class="flex flex-1 justify-between sm:hidden">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center rounded-md border border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
        >
          Previous
        </button>
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700 dark:text-dark-200">
            Showing <span class="font-medium">{{ totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1 }}</span> to
            <span class="font-medium">{{ Math.min(currentPage * perPage, totalItems) }}</span> of
            <span class="font-medium">{{ totalItems }}</span> results
          </p>
        </div>
        <div>
          <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-dark-400 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
            >
              <span class="sr-only">Previous</span>
              <i class="fas fa-chevron-left h-5 w-5"></i>
            </button>
            <button
              v-for="page in displayedPages"
              :key="page"
              @click="typeof page === 'number' ? goToPage(page) : null"
              :class="[
                typeof page === 'number' 
                  ? (page === currentPage
                      ? 'relative z-10 inline-flex items-center bg-blue-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-dark-100 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0')
                  : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-500 dark:text-dark-400 cursor-default'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-dark-400 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }"
            >
              <span class="sr-only">Next</span>
              <i class="fas fa-chevron-right h-5 w-5"></i>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Order Preview Modal -->
    <div v-if="showOrderPreview" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75"></div>
        </div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="inline-block align-bottom bg-white dark:bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <!-- Header -->
          <div class="bg-gray-50 dark:bg-dark-700 px-4 py-3 sm:px-6 border-b border-gray-200 dark:border-dark-600">
            <div class="flex items-center justify-between">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-dark-100">
                Order Details
              </h3>
              <button
                @click="closeOrderPreview"
                class="bg-white dark:bg-dark-800 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-dark-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span class="sr-only">Close</span>
                <i class="fas fa-times h-6 w-6"></i>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="px-4 py-5 sm:p-6">
            <div v-if="orderPreviewLoading" class="flex justify-center items-center py-8">
              <div class="text-center">
                <i class="fas fa-spinner fa-spin text-blue-500 text-lg mb-2"></i>
                <p class="text-sm text-gray-600 dark:text-dark-400">Loading order details...</p>
              </div>
            </div>
            
            <div v-else-if="selectedOrder" class="space-y-4">
              <!-- Order Summary -->
              <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100 mb-3">Order Summary</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Order #:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">#{{ selectedOrder.order_number || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Client:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">{{ selectedOrder.client_name || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Order Date:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">
                      {{ formatDate(selectedOrder.created_at) }}
                    </span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Due Date:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">
                      {{ formatDate(selectedOrder.deadline) }}
                    </span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Status:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">
                      <span class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                        {{ selectedOrder.status || 'N/A' }}
                      </span>
                    </span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Payment Status:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">
                      <span class="px-2 py-1 text-xs font-medium rounded-full" 
                            :class="getPaymentStatusClass(selectedOrder)">
                        {{ getPaymentStatusText(selectedOrder) }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Financial Summary -->
              <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100 mb-3">Financial Summary</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Total Amount:</span>
                    <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">₱{{ formatPrice(selectedOrder.total_amount || 0) }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Amount Paid:</span>
                    <span class="ml-2 font-medium text-green-600 dark:text-green-400">₱{{ formatPrice(calculateTotalPaid()) }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Amount Due:</span>
                    <span class="ml-2 font-medium text-red-600 dark:text-red-400">₱{{ formatPrice(selectedOrder.amount_due || 0) }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-dark-400">Days Overdue:</span>
                    <span class="ml-2 font-medium text-red-600 dark:text-red-400">{{ selectedOrder.days_old || 0 }} days</span>
                  </div>
                </div>
              </div>

              <!-- Order Items -->
              <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100 mb-3">Order Items</h4>
                <div v-if="selectedOrder.items && selectedOrder.items.length > 0" class="space-y-2">
                  <div v-for="item in selectedOrder.items" :key="item.id" class="flex justify-between items-center text-sm border-b border-gray-200 dark:border-dark-600 pb-2">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900 dark:text-dark-100">{{ item.product_name || item.description }}</div>
                    </div>
                    <div class="flex-1">
                      <div class="text-gray-500 dark:text-dark-400">Qty: {{ item.quantity }} × ₱{{ formatPrice(item.unit_price || 0) }}</div>
                    </div>
                    <div class="text-right">
                      <div class="font-medium text-gray-900 dark:text-dark-100">₱{{ formatPrice(calculateItemTotal(item)) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-600 dark:text-dark-400">
                  <p>Order items not available</p>
                </div>
              </div>

              <!-- Payment History -->
              <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100 mb-3">Payment History</h4>
                <div v-if="selectedOrder.payments && selectedOrder.payments.length > 0" class="space-y-2">
                  <div v-for="payment in selectedOrder.payments" :key="payment.id" class="flex justify-between items-center text-sm border-b border-gray-200 dark:border-dark-600 pb-2">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900 dark:text-dark-100">{{ payment.payment_method || 'Payment' }}</div>
                      
                    </div>
                    <div class="flex-1">
                      <div class="text-gray-500 dark:text-dark-400">{{ formatDate(payment.payment_date) }}</div>
                    </div>
                    <div class="text-right">
                      <div class="font-medium text-green-600 dark:text-green-400">₱{{ formatPrice(payment.amount) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-600 dark:text-dark-400">
                  <p>No payment history available</p>
                </div>
              </div>

              <!-- Additional Details -->
              <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100 mb-3">Additional Details</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div v-if="selectedOrder.notes">
                    <span class="text-gray-500 dark:text-dark-400">Notes:</span>
                    <span class="ml-2 text-gray-900 dark:text-dark-100">{{ selectedOrder.notes }}</span>
                  </div>
                  <div v-if="selectedOrder.deliveryAddress">
                    <span class="text-gray-500 dark:text-dark-400">Delivery Address:</span>
                    <span class="ml-2 text-gray-900 dark:text-dark-100">{{ selectedOrder.deliveryAddress }}</span>
                  </div>
                  <div v-if="selectedOrder.contactPerson">
                    <span class="text-gray-500 dark:text-dark-400">Contact Person:</span>
                    <span class="ml-2 text-gray-900 dark:text-dark-100">{{ selectedOrder.contactPerson }}</span>
                  </div>
                  <div v-if="selectedOrder.contactNumber">
                    <span class="text-gray-500 dark:text-dark-400">Contact Number:</span>
                    <span class="ml-2 text-gray-900 dark:text-dark-100">{{ selectedOrder.contactNumber }}</span>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-600">
                <button
                  @click="closeOrderPreview"
                  class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-500 rounded-md hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
                <button
                  @click="recordPayment(selectedOrder.id)"
                  class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Record Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useApi } from '@/composables/useApi';
import { useRouter } from 'vue-router';
import { useToast } from '@/composables/useToast';
import debounce from 'lodash/debounce';

const api = useApi();
const router = useRouter();
const toast = useToast();

// State
const followUps = ref([]);
const loading = ref(false);
const filters = ref({
  search: '',
  sort: 'oldest',
  urgency: 'all'
});
const currentPage = ref(1);
const perPage = ref(10);
const totalItems = ref(0);

// Order preview state
const showOrderPreview = ref(false);
const selectedOrder = ref(null);
const orderPreviewLoading = ref(false);

// Computed properties
const totalPages = computed(() => {
  return Math.ceil(totalItems.value / perPage.value) || 1;
});

const displayedPages = computed(() => {
  const pages = [];
  const maxPagesToShow = 5;
  
  if (totalPages.value <= maxPagesToShow) {
    // Show all pages if there are only a few
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);
    
    // Calculate middle pages
    let startPage = Math.max(2, currentPage.value - 1);
    let endPage = Math.min(totalPages.value - 1, currentPage.value + 1);
    
    // Adjust if we're at the edges
    if (currentPage.value <= 2) {
      endPage = 3;
    } else if (currentPage.value >= totalPages.value - 1) {
      startPage = totalPages.value - 2;
    }
    
    // Add ellipsis if needed
    if (startPage > 2) {
      pages.push('...');
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < totalPages.value - 1) {
      pages.push('...');
    }
    
    // Always show last page
    pages.push(totalPages.value);
  }
  
  return pages;
});

// Methods
const fetchData = async () => {
  try {
    loading.value = true;
    console.log('Fetching data with filters:', filters.value);
    
    // Prepare params
    const params = {
      page: currentPage.value,
      limit: perPage.value,
      sort: filters.value.sort
    };
    
    // Only add non-empty search term
    if (filters.value.search && filters.value.search.trim() !== '') {
      params.search = filters.value.search.trim();
    }
    
    // Only add urgency filter if it's not 'all'
    if (filters.value.urgency && filters.value.urgency !== 'all') {
      params.urgency = filters.value.urgency;
    }
    
    console.log('Request params:', params);
    const response = await api.get('/dashboard/follow-ups', { params });
    
    if (response.data) {
      console.log('Response data:', response.data);
      followUps.value = response.data.data || [];
      totalItems.value = response.data.pagination?.total || 0;
    }
  } catch (error) {
    console.error('Error fetching follow-ups:', error);
    toast.error('Failed to load payment follow-ups');
  } finally {
    loading.value = false;
  }
};

const debouncedFetch = debounce(() => {
  currentPage.value = 1; // Reset to first page when search changes
  fetchData();
}, 300);

const goToPage = (page) => {
  if (typeof page === 'number' && page !== currentPage.value && page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchData();
  }
};

const viewOrder = async (orderId) => {
  try {
    orderPreviewLoading.value = true;
    
    // First, get the basic order info from current list
    const basicOrder = followUps.value.find(followup => followup.id === orderId);
    if (!basicOrder) {
      toast.error('Order not found in current list');
      return;
    }
    
    // Set basic info immediately for better UX
    selectedOrder.value = {
      ...basicOrder,
      // Add missing fields with fallbacks
      orderDate: basicOrder.dateReceived || 'N/A',
      dueDate: 'N/A', // Will be updated if available
      status: 'ready_release', // From the context
      paymentStatus: basicOrder.amountDue > 0 ? 'Partial' : 'Paid',
      items: [],
      payments: basicOrder.amountPaid > 0 ? [{
        id: 'temp',
        amount: basicOrder.amountPaid,
        paymentMethod: 'Payment',
        paymentDate: basicOrder.dateReceived,
        createdAt: basicOrder.dateReceived
      }] : []
    };
    showOrderPreview.value = true;
    
    // Try to fetch complete order details from API
    try {
      console.log('Fetching detailed order info for ID:', orderId);
      const response = await api.get(`/orders/${orderId}`);
      
      if (response.data) {
        console.log('=== FULL API RESPONSE ===');
        console.log('Response data:', response.data);
        console.log('Response data keys:', Object.keys(response.data));
        console.log('Response data type:', typeof response.data);
        console.log('=== END API RESPONSE ===');
        
        // Merge API data with basic data, keeping basic data as fallback
        selectedOrder.value = {
          ...selectedOrder.value,
          ...response.data,
          // Ensure we keep the calculated values from follow-ups
          amount_due: basicOrder.amountDue,
          days_old: basicOrder.days_old,
          // Use exact field names from OrderPreview.vue
          order_number: response.data.order_number || basicOrder.orderNumber,
          client_name: response.data.client_name || basicOrder.clientName,
          total_amount: response.data.total_amount || basicOrder.totalAmount,
          amount_paid: response.data.amount_paid || basicOrder.amountPaid,
          // Use exact field names from OrderPreview.vue
          created_at: response.data.created_at || basicOrder.dateReceived || 'N/A',
          deadline: response.data.deadline || 'N/A',
          status: response.data.status || 'ready_release',
          payment_status: response.data.payment_status || (basicOrder.amountDue > 0 ? 'Partial' : 'Paid'),
          items: response.data.items || [],
          payments: response.data.payments || (basicOrder.amountPaid > 0 ? [{
            id: 'temp',
            amount: basicOrder.amountPaid,
            payment_method: 'Payment',
            payment_date: basicOrder.dateReceived
          }] : []),
          notes: response.data.notes || '',
          department: response.data.department || null,
          order_type: response.data.order_type || null,
          assigned_to_user_name: response.data.assigned_to_user_name || null,
          created_by_user_name: response.data.created_by_user_name || null,
          image_url: response.data.image_url || null
        };
        
        console.log('Final selectedOrder:', selectedOrder.value);
      }
    } catch (apiError) {
      console.error('Error fetching detailed order info:', apiError);
      // Keep the basic info we already set
      toast.warning('Showing basic order information. Some details may be incomplete.');
    }
    
  } catch (error) {
    console.error('Error in viewOrder:', error);
    toast.error('Failed to load order details');
  } finally {
    orderPreviewLoading.value = false;
  }
};

const recordPayment = (orderId) => {
  emit('record-payment', orderId);
};

const closeOrderPreview = () => {
  showOrderPreview.value = false;
  selectedOrder.value = null;
};

const getUrgencyClass = (days_old) => {
  if (days_old > 90) {
    return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400';
  } else if (days_old > 60) {
    return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400';
  } else if (days_old > 30) {
    return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400';
  } else {
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
};

const formatDate = (dateValue) => {
  if (!dateValue || dateValue === 'N/A' || dateValue === '') {
    return 'N/A';
  }
  
  try {
    // Handle different date formats
    let date;
    if (typeof dateValue === 'string') {
      // Try to parse the date string
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return 'N/A';
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Format the date
    return date.toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error, dateValue);
    return 'N/A';
  }
};

const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const calculateItemTotal = (item) => {
  // Try to get total_price from the item first
  if (item.total_price && item.total_price > 0) {
    return item.total_price;
  }
  
  // If total_price is not available or is 0, calculate it from quantity and unit_price
  const quantity = parseFloat(item.quantity || 0);
  const unitPrice = parseFloat(item.unit_price || 0);
  
  return quantity * unitPrice;
};

const calculateTotalPaid = () => {
  if (!selectedOrder.value || !selectedOrder.value.payments) {
    return 0;
  }
  
  // Calculate total from payments array
  const totalPaid = selectedOrder.value.payments.reduce((sum, payment) => {
    return sum + parseFloat(payment.amount || 0);
  }, 0);
  
  return totalPaid;
};

const getPaymentStatusText = (order) => {
  if (!order) return 'N/A';
  
  // Use the same logic as OrderCompletionApprovals.vue
  if (order.payment_discrepancy === 0 && order.total_paid > 0) {
    return 'Fully Paid';
  } else if (order.total_paid === 0) {
    return 'No Payment';
  } else if (order.payment_discrepancy > 0) {
    return 'Underpaid';
  } else if (order.payment_discrepancy < 0) {
    return 'Overpaid';
  } else {
    return 'Partial';
  }
};

const getPaymentStatusClass = (order) => {
  if (!order) return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
  
  // Use the same logic as OrderCompletionApprovals.vue
  if (order.payment_discrepancy === 0 && order.total_paid > 0) {
    return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400';
  } else if (order.total_paid === 0) {
    return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
  } else if (order.payment_discrepancy > 0) {
    return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400';
  } else if (order.payment_discrepancy < 0) {
    return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400';
  } else {
    return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400';
  }
};

// Setup
onMounted(() => {
  fetchData();
});

// Watch for filter changes
watch([() => filters.value.sort, () => filters.value.urgency], () => {
  currentPage.value = 1; // Reset to first page
  fetchData();
}, { deep: true });

// Define events
const emit = defineEmits(['view-order', 'record-payment']);
</script> 